import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';

const OutreachScheduledTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const data = await ApiService.getScheduledTasks();
      setTasks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleCancelTask = async (id: string) => {
    try {
      await ApiService.cancelScheduledTask(id);
      fetchTasks();
    } catch (err: any) {
      alert(`Error cancelling task: ${err.message}`);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active': return <Chip label="ACTIVE" color="primary" size="small" />;
      case 'completed': return <Chip label="COMPLETED" color="success" size="small" />;
      case 'cancelled': return <Chip label="CANCELLED" color="error" size="small" />;
      default: return <Chip label={status.toUpperCase()} size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Scheduled Validation Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your throttled email validation tasks (4,500/day limit)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            BACK
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTasks}>
            REFRESH
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Task Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Total Emails</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <LinearProgress sx={{ width: '100%', mb: 1 }} />
                  Loading tasks...
                </TableCell>
              </TableRow>
            ) : tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No scheduled tasks found.
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id}>
                  <TableCell>{task.name}</TableCell>
                  <TableCell>{task.total_count}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(task.completed_count / task.total_count) * 100 || 0} 
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="caption">
                        {task.completed_count || 0} / {task.total_count}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(task.status)}</TableCell>
                  <TableCell>{new Date(task.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    {task.status === 'active' && (
                      <Tooltip title="Cancel Task">
                        <IconButton color="error" onClick={() => handleCancelTask(task.id)}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OutreachScheduledTasksPage;
